import type FileReference from "@/managers/data/file/fileReference"
import {firstOrNull} from "@/utility/structure/array"

const videoExtensions = ["mp4", "m4v", "webm", "mpq", "avi", "3gs", "3g", "mkv", "asf", "flv", "mov", "wmv", "ogg"]
const imageExtensions = ["jpg", "jpeg", "png", "gif", "tiff", "bmp"]
const fileNameMask = /(?:.+\/)?(.+?)\.(\w+)$/

export const imageMimeTypePrefix = "image/"
export const videoMimeTypePrefix = "video/"

export enum MimeType {
	unknownType = "application/octet-stream",
	gif = "image/gif",
	jpg = "image/jpeg",
	mov = "video/quicktime",
	mp4 = "video/mp4",
	mkv = "video/x-matroska"
}

export function getExtension(path: string): string | null {
	const result = fileNameMask.exec(path)

	if (result === null)
		return null

	return result[2].toLocaleLowerCase()
}

export function getExtensionFromType(type: string): string | null {
	switch (type) {
		case MimeType.jpg:
			return "jpg"
		case MimeType.gif:
			return "gif"
		case MimeType.mov:
			return "mov"
		case MimeType.mp4:
			return "mp4"
		case MimeType.mkv:
			return "mkv"
	}

	for (const prefix of [imageMimeTypePrefix, videoMimeTypePrefix])
		if (type.indexOf(prefix) === 0)
			return type.slice(prefix.length)

	return null
}

export function getName(name: string): string {
	const result = fileNameMask.exec(name)

	if (result === null)
		throw new Error("Failed to parse file name: " + name)

	return result[1]
}

export function getType(extension: string | null): string {
	if (extension !== null) {
		switch (extension) {
			case "jpg":
			case "jpeg":
				return MimeType.jpg
			case "mov":
				return MimeType.mov
			case "mp4":
			case "m4v":
				return MimeType.mp4
			case "mkv":
				return MimeType.mkv
		}

		if (imageExtensions.some(img => img === extension))
			return imageMimeTypePrefix + extension
		if (videoExtensions.some(img => img === extension))
			return videoMimeTypePrefix + extension
	}
	return MimeType.unknownType
}

export function isImage(type: string): boolean {
	return type.startsWith(imageMimeTypePrefix)
}

export function isVideo(type: string): boolean {
	return type.startsWith(videoMimeTypePrefix)
}

export function blobToFile(name: string, blob: Blob, type?: string): File {
	return new File([blob], name, {type: type ?? blob.type})
}

export function downloadFile(data: Blob, name: string, type?: string): void {
	const file = blobToFile(name, data, type)
	const a = document.createElement("a")
	a.href = URL.createObjectURL(file)
	a.setAttribute("download", name)
	a.click()
	a.remove()
	URL.revokeObjectURL(a.href)
}

export function dataUriToBlob(data: string): Blob {
	const byteString = atob(data.split(",")[1])
	const mimeString = data.split(",")[0].split(":")[1].split(";")[0]
	const ab = new ArrayBuffer(byteString.length)
	const ia = new Uint8Array(ab)

	for (let i = 0; i < byteString.length; i++)
		ia[i] = byteString.charCodeAt(i)

	return new Blob([ab], {type: mimeString})
}

export async function fileFromUrl(url: string): Promise<File> {
	const response = await fetch(url, {mode: "cors"})

	if (!response.ok)
		throw new Error(`Failed to download file: ${response.statusText}`)

	const blob = await response.blob()
	const typeHeader = response.headers.get("Content-Type")

	let extension = getExtension(url)
		?? getExtensionFromType(blob.type)
		?? typeHeader !== null
			? getExtensionFromType(typeHeader!)
			: null
	extension = extension !== null ? "." + extension : ""

	return blobToFile("file" + extension, blob)
}

export function getThumbnailUrl(files: FileReference[]): string | null {
	return firstOrNull(files, f => f.isImage && f.hasThumbnail)?.thumbnailUrl
		?? firstOrNull(files, f => f.hasThumbnail)?.thumbnailUrl
		?? firstOrNull(files, f => f.isImage)?.url
		?? firstOrNull(files, f => f.isGif)?.url
		?? null
}
